import { LuBookOpen, LuCheck, LuTimer, LuX } from "react-icons/lu";
import { useEffect, useRef, useState } from "react";
import { DIFFICULTY_LABEL, subjectName } from "../../shared/constants";
import { ReadAloud } from "./ReadAloud";

export interface QView {
  qid: string;
  kind: "mc" | "tf" | "short";
  text: string;
  options: string[];
  subject: string;
  topic: string;
  difficulty: 1 | 2 | 3;
  pid: string;
  issuedAt?: number;
  deadline?: number;
  answered?: boolean;
}
export interface FB {
  correct: boolean;
  timedOut?: boolean;
  correctText: string;
  explanation: string;
  counted: boolean;
}

/** Remaining time derived from SERVER time: the client only shows it, the server decides. */
export function useCountdown(deadline: number | undefined, serverNow: number | undefined) {
  const offset = useRef(0);
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (serverNow) offset.current = serverNow - Date.now();
  }, [serverNow]);
  useEffect(() => {
    if (!deadline) return setLeft(0);
    const tick = () => setLeft(Math.max(0, deadline - (Date.now() + offset.current)));
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [deadline]);
  return left;
}

/**
 * `reveal` turns the card into the "after the answer" view: nothing can be clicked, the right option lights up green, a wrong pick is
 * marked softly in orange and the explanation sits underneath – so every answer becomes a small moment instead of the card just vanishing.
 */
export interface Reveal { given: number | string | null; fb: FB; correctIdx?: number }
export function revealFor(q: QView, given: number | string | null, fb: FB): Reveal {
  const idx = q.options.findIndex((o) => o === fb.correctText);
  return { given, fb, correctIdx: idx >= 0 ? idx : undefined };
}

export function QuestionCard(props: { q: QView; serverNow?: number; onAnswer: (given: number | string) => void; busy?: boolean; feedback?: FB | null; showTimer?: boolean; totalMs?: number; locked?: boolean; chosen?: number | null; reveal?: Reveal | null }) {
  const { q, onAnswer, busy } = props;
  const rv = props.reveal ?? null;
  const feedback = props.feedback ?? rv?.fb ?? null;
  const left = useCountdown(q.deadline, props.serverNow);
  const total = props.totalMs ?? Math.max(1, (q.deadline ?? 0) - (q.issuedAt ?? 0));
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLInputElement>(undefined as unknown as HTMLInputElement);
  useEffect(() => {
    setText("");
    if (q.kind === "short") inputRef.current?.focus();
  }, [q.pid, q.kind]);
  const [picked, setPicked] = useState<number | null>(null);
  useEffect(() => setPicked(null), [q.pid]);
  const disabled = !!busy || !!props.locked || !!feedback || !!q.answered || !!rv;

  // keyboard: 1-4 select an answer
  useEffect(() => {
    if (q.kind === "short" || disabled) return;
    const h = (e: KeyboardEvent) => {
      const n = Number(e.key);
      if (n >= 1 && n <= q.options.length && !(e.target as HTMLElement)?.matches?.("input,textarea,select")) onAnswer(n - 1);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [q.pid, q.options.length, q.kind, disabled, onAnswer]);

  return (
    <div className={rv ? `qreveal ${rv.fb.correct ? "ok" : "no"}` : "qcard"} aria-live="polite">
      <div className="qmeta">
        <span className="subjchip"><LuBookOpen size={18} aria-hidden="true" /> {subjectName(q.subject)} – {q.topic}</span>
        <span className={`pill d${q.difficulty}`}>{DIFFICULTY_LABEL[q.difficulty]}</span>
        {!rv && !q.answered && <ReadAloud q={q} id={q.pid} />}
        {!rv && props.showTimer !== false && q.deadline && (
          <span className={`pill ${left > 0 && left / total < 0.25 ? "low" : ""}`} aria-label={`${Math.ceil(left / 1000)} sekunder tilbage`}>
            <LuTimer aria-hidden="true" size={16} /> {Math.ceil(left / 1000)} s
          </span>
        )}
      </div>
      {!rv && props.showTimer !== false && q.deadline && (
        <div className={`timer ${left > 0 && left / total < 0.25 ? "low" : ""}`} aria-hidden="true">
          <i style={{ width: "100%", transform: `scaleX(${Math.max(0, Math.min(1, left / total))})` }} />
        </div>
      )}
      <div className="qtext" id={`q-${q.pid}`}>
        {q.text}
      </div>
      {q.kind === "short" ? (
        <form
          onSubmit={(e: any) => {
            e.preventDefault();
            if (text.trim() && !disabled) onAnswer(text.trim());
          }}
          className="row"
        >
          <input ref={inputRef} value={rv && typeof rv.given === "string" ? rv.given : text} onChange={(e: any) => setText(e.target.value)} className={rv ? (rv.fb.correct ? "inok" : "inno") : ""} placeholder="Skriv dit svar" aria-labelledby={`q-${q.pid}`} disabled={disabled} maxLength={100} autoComplete="off" style={{ flex: 1, minWidth: 180 }} />
          <button className="btn" disabled={disabled || !text.trim()}>
            Svar
          </button>
        </form>
      ) : (
        <div className="answers" role="group" aria-labelledby={`q-${q.pid}`}>
          {q.options.map((o, i) => (
            <button key={i} className={`ans ${(props.chosen ?? picked) === i && !rv ? "sel" : ""} ${rv && rv.correctIdx === i ? "good" : ""} ${rv && !rv.fb.correct && rv.given === i ? "badc" : ""} ${rv && rv.correctIdx !== i && rv.given !== i ? "dim" : ""}`} disabled={disabled} onClick={() => { setPicked(i); onAnswer(i); }}>
              <span className="key" aria-hidden="true">
                {rv && rv.correctIdx === i ? <LuCheck size={18} /> : rv && !rv.fb.correct && rv.given === i ? <LuX size={18} /> : "ABCDEF"[i]}
              </span>
              <span>{o}</span>
            </button>
          ))}
        </div>
      )}
      {feedback && <FeedbackBox fb={feedback} />}
    </div>
  );
}

export function FeedbackBox({ fb }: { fb: FB }) {
  return (
    <div className={`fbbox feedback ${fb.correct ? "good" : "bad"}`} role="status">
      <span className="fbicon" aria-hidden="true">{fb.correct ? <LuCheck size={26} /> : <LuX size={26} />}</span>
      <div className="fbtext">
        <p className="fbtitle">{fb.correct ? "Korrekt!" : fb.timedOut ? "Tiden løb ud!" : "Næsten!"}</p>
        {!fb.correct && (
          <p>
            Det rigtige svar er: <strong>{fb.correctText}</strong>
          </p>
        )}
        <p style={{ margin: 0 }}>{fb.explanation}</p>
        {!fb.counted && <small className="muted">Du har set det spørgsmål for nylig, så det giver ikke mønter eller XP denne gang.</small>}
      </div>
    </div>
  );
}
